import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useT } from "../i18n";
import { fetchGenerators, listProjects, recentFiles } from "../api";
import { Icon } from "./Icon";

interface Item {
  kind: "page" | "project" | "file" | "generator";
  label: string;
  sub: string;
  ico: string;
  to: string;
}

const PAGES: Item[] = [
  { kind: "page", label: "Dashboard", sub: "Atelier", ico: "dashboard", to: "/dashboard" },
  { kind: "page", label: "Créer", sub: "Atelier", ico: "sparkle", to: "/create" },
  { kind: "page", label: "Projets", sub: "Atelier", ico: "projects", to: "/projects" },
  { kind: "page", label: "Studio 3D", sub: "Atelier", ico: "studio", to: "/studio" },
  { kind: "page", label: "Générateurs", sub: "Création", ico: "generators", to: "/generators" },
  { kind: "page", label: "Production · Monitoring", sub: "Production", ico: "route", to: "/production" },
  { kind: "page", label: "Imprimantes", sub: "Système", ico: "printer", to: "/printers" },
  { kind: "page", label: "Filaments", sub: "Système", ico: "box", to: "/filaments" },
  { kind: "page", label: "Intégrations", sub: "Système", ico: "plug", to: "/integrations" },
  { kind: "page", label: "Communauté", sub: "Compte", ico: "users", to: "/community" },
  { kind: "page", label: "Profil", sub: "Compte", ico: "user", to: "/profile" },
  { kind: "page", label: "Réglages", sub: "Compte", ico: "shield", to: "/settings" },
];

const KIND_LABEL: Record<Item["kind"], string> = {
  page: "Pages",
  project: "Projets",
  file: "Fichiers",
  generator: "Générateurs",
};

// Global quick-search / command palette (⌘K). Indexes pages, projects, recent files
// (open straight into the Studio) and generators; arrow keys + Enter to navigate.
export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const tr = useT();
  const [items, setItems] = useState<Item[]>(PAGES);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const nav = useNavigate();

  // Build the search index when the palette opens (cheap, cached for the session length).
  useEffect(() => {
    if (!open) return;
    setQ(""); setActive(0);
    inputRef.current?.focus();
    Promise.allSettled([listProjects(), recentFiles(50), fetchGenerators()]).then(([p, f, g]) => {
      const idx: Item[] = [...PAGES];
      if (p.status === "fulfilled") {
        for (const proj of p.value)
          idx.push({ kind: "project", label: proj.name, sub: proj.group || `${proj.file_count ?? 0} fichier(s)`, ico: "projects", to: `/projects/${proj.id}` });
      }
      if (f.status === "fulfilled") {
        for (const file of f.value)
          idx.push({ kind: "file", label: file.name, sub: `${file.project_name} · ${file.material}`, ico: "box", to: `/studio?project=${file.project_id}&file=${file.id}` });
      }
      if (g.status === "fulfilled") {
        for (const gen of g.value)
          idx.push({ kind: "generator", label: gen.title, sub: gen.name, ico: "generators", to: "/generators" });
      }
      setItems(idx);
    });
  }, [open]);

  const results = useMemo(() => {
    const ql = q.trim().toLowerCase();
    const list = !ql ? items : items.filter((i) => `${i.label} ${i.sub}`.toLowerCase().includes(ql));
    return list.slice(0, 40);
  }, [items, q]);

  useEffect(() => { setActive(0); }, [q]);

  if (!open) return null;

  function go(item: Item) {
    nav(item.to);
    onClose();
  }
  function onKey(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (results[active]) go(results[active]); }
    else if (e.key === "Escape") { e.preventDefault(); onClose(); }
  }

  // Group results by kind for section headers, keeping a flat index for keyboard nav.
  let flat = -1;
  const groups: { kind: Item["kind"]; rows: { item: Item; i: number }[] }[] = [];
  for (const item of results) {
    flat++;
    const g = groups.find((x) => x.kind === item.kind);
    const row = { item, i: flat };
    if (g) g.rows.push(row);
    else groups.push({ kind: item.kind, rows: [row] });
  }

  return (
    <div className="cmdk-backdrop" onClick={onClose}>
      <div className="cmdk" onClick={(e) => e.stopPropagation()}>
        <div className="cmdk-input">
          <Icon name="search" size={16} />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKey}
            placeholder={tr("Rechercher projets, fichiers, générateurs, pages…")}
          />
          <span className="kbd">Esc</span>
        </div>
        <div className="cmdk-list">
          {results.length === 0 ? (
            <div className="cmdk-empty">Aucun résultat pour « {q} »</div>
          ) : (
            groups.map((g) => (
              <div key={g.kind}>
                <div className="cmdk-section">{KIND_LABEL[g.kind]}</div>
                {g.rows.map(({ item, i }) => (
                  <button
                    key={i}
                    className={`cmdk-row ${i === active ? "on" : ""}`}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => go(item)}
                  >
                    <span className="cmdk-ico"><Icon name={item.ico} size={15} /></span>
                    <span className="cmdk-label">{item.label}</span>
                    <span className="cmdk-sub">{item.sub}</span>
                    {item.kind === "file" && <span className="kbd">Studio</span>}
                  </button>
                ))}
              </div>
            ))
          )}
        </div>
        <div className="cmdk-foot">
          <span><span className="kbd">↑↓</span> naviguer</span>
          <span><span className="kbd">↵</span> ouvrir</span>
          <span><span className="kbd">esc</span> fermer</span>
        </div>
      </div>
    </div>
  );
}
